import { User } from '../types/user';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

// Cache for users to avoid repeated Firebase calls
const userCache = new Map<string, User>();

/**
 * Get user display name for a given user ID
 * First checks current user, then cache, then Firebase
 */
export const getUserDisplayName = async (
  userId: string, 
  currentUser?: User | null
): Promise<string> => {
  // If this is the current user, return their name
  if (currentUser && currentUser.id === userId) {
    return currentUser.displayName || currentUser.email || 'Current User';
  }

  // Check cache first
  if (userCache.has(userId)) {
    const user = userCache.get(userId)!;
    return user.displayName || user.email || 'Unknown User';
  }

  // Fetch from Firebase and cache the result
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data() as User;
      const user: User = {
        ...userData,
        id: userDoc.id,
        createdAt: userData.createdAt,
        updatedAt: userData.updatedAt,
        lastLoginAt: userData.lastLoginAt
      };
      
      // Add to cache
      userCache.set(userId, user);
      
      return user.displayName || user.email || 'Unknown User';
    }
  } catch (error) {
    console.error('Error fetching user by ID:', error);
  }
  
  // Fallback for unknown users
  return userId ? `User-${userId.substring(0, 8)}...` : 'Unknown User';
};

/**
 * Synchronous version that only checks current user and cache
 * Use this when you can't use async/await
 */
export const getUserDisplayNameSync = (
  userId: string, 
  currentUser?: User | null
): string => {
  // If this is the current user, return their name
  if (currentUser && currentUser.id === userId) {
    return currentUser.displayName || currentUser.email || 'Current User';
  }

  // Check cache
  if (userCache.has(userId)) {
    const user = userCache.get(userId)!;
    return user.displayName || user.email || 'Unknown User';
  }
  
  // Fallback for unknown users
  return userId ? `User-${userId.substring(0, 8)}...` : 'Unknown User';
};

/**
 * Preload user data into cache
 */
export const preloadUserData = (user: User) => {
  userCache.set(user.id, user);
};

/**
 * Clear user cache
 */
export const clearUserCache = () => {
  userCache.clear();
}; 